import type { VoidFunctionComponent } from 'react';
import { connect } from 'react-redux';
import ProjectGroupModule from '../../../redux/project_grouping';
import type {
  ProjectGroup,
  ProjectGroupRelease,
} from '../../../types';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import { fossaPrimaryFont } from '../../../theme/typography';
import ProjectGroupNavReleaseSwitcher from './components/ProjectGroupNavReleaseSwitcher';
import COLORS from '../../../theme/colors';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { DISABLE_RELEASE_GROUPS_REVAMP, SBOM_PORTAL_PRIVATE } from '../../../../shared/featureFlags/flagNames';
import { Badge, BadgeColors, BadgeShapes, FeatureFlagRequired } from '../../generic';
import ReleaseGroupsReleaseSelector from './components/ReleaseSelector';
import { useOnUnmount, useToggle } from '../../../hooks';

type OwnProps = {
  projectGroupId: number;
  projectGroup: ProjectGroup;
  location: any;
};

type StateProps = {
  selectedRelease: ProjectGroupRelease;
};

type DispatchProps = {
  onSelectRelease: (projectGroupId: number, releaseId: number) => void;
};

type ProjectGroupNavProps = OwnProps & StateProps & DispatchProps;

const ProjectGroupNav: VoidFunctionComponent<ProjectGroupNavProps> = ({
  projectGroup,
  selectedRelease,
  projectGroupId,
  onSelectRelease,
  location,
}) => {
  const [isToggled, onToggle] = useToggle();
  const isReleaseGroupRevampEnabled = !useIsFeatureEnabled(DISABLE_RELEASE_GROUPS_REVAMP);
  const handleSelectRelease = (releaseId: number) => {
    onSelectRelease(projectGroup.id, releaseId);
    onToggle(); // Close the switcher
  };
  const currentReleaseTitle = selectedRelease?.title ?? projectGroup.releases[0]?.title;
  const currentReleaseId = selectedRelease?.id ?? projectGroup.releases[0]?.id;

  //  We hide the releases selector if we are on the releases or settings tabs.
  const isReleaseTab = location?.pathname?.endsWith('releases');
  const isSettingsTab = location?.pathname?.includes('settings/');
  const shouldHideReleaseSelector = isReleaseTab || isSettingsTab;

  //  Ensure that the dropdown is closed when the component unmounts.
  useOnUnmount(() => {
    if (isToggled) {
      onToggle();
    }
  });

  return (
    <div>
      <div style={{
        textTransform: 'uppercase',
        color: 'rgba(231, 236, 243, 0.6)',
        fontWeight: '600',
        fontSize: '12px',
        marginBottom: '4px',
      }}>
        Release Group
      </div>
      <div style={{ marginBottom: '34px' }}>
        <Flex justify="flex-start">
          <h1 style={{ margin: 0, display: 'flex' }}>
            <span className="margin-right-sm" style={{ fontSize: '24px', color: '#90A0B6', marginTop: '4px' }}>
              <Icon icon={icons.folderOpenO} />
            </span>
            {projectGroup.title || <em>Loading...</em>}
          </h1>
          {projectGroup && !shouldHideReleaseSelector && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="margin-left-sm margin-right-sm">
                <Icon icon={icons.angleRight} />
              </span>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    color: COLORS.fossaGreen,
                    fontSize: '24px',
                    fontFamily: fossaPrimaryFont,
                    padding: '12px 0',
                    cursor: 'pointer',
                  }}
                  onClick={onToggle}
                >
                  {currentReleaseTitle}
                  <span className="margin-left-sm" style={{ fontSize: '12px' }}>
                    <Icon icon={icons.caretDown} />
                  </span>
                </div>
                {isToggled && (
                  isReleaseGroupRevampEnabled ? (
                    <ReleaseGroupsReleaseSelector
                      projectGroupId={projectGroupId}
                      selectedReleaseId={currentReleaseId}
                      onReleaseSelect={handleSelectRelease}
                      onClose={onToggle}
                    />
                  ) : (
                    <ProjectGroupNavReleaseSwitcher
                      onEscPress={onToggle}
                      projectGroupId={projectGroupId}
                      releases={projectGroup.releases}
                      currentRelease={selectedRelease || projectGroup.releases[0]}
                      onReleaseSelect={handleSelectRelease}
                    />
                  )
                )}
              </div>
              {
                !projectGroup.publicOnPortal &&
                <FeatureFlagRequired flagName={SBOM_PORTAL_PRIVATE}>
                  <div className="margin-left-sm">
                    <Badge color={BadgeColors.DarkGray} shape={BadgeShapes.Circle}>
                      <Icon icon={icons.lockOutline} /> Private
                    </Badge>
                  </div>
                </FeatureFlagRequired>
              }
            </div>
          )}
        </Flex>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps: OwnProps) => ({
  selectedRelease: ProjectGroupModule.selectors.getSelectedRelease(state, ownProps),
});

const mapDispatchToProps: DispatchProps = {
  onSelectRelease: ProjectGroupModule.actions.selectRelease,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProjectGroupNav);
